import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { PageHeader } from "../components/ui/kaizen/PageHeader";
import { StatCard } from "../components/ui/kaizen/StatCard";
import { EmptyState } from "../components/ui/kaizen/EmptyState";
import { CurrencyDollar, Trophy, Barbell, Gift, ArrowsClockwise, Receipt, DownloadSimple } from "@phosphor-icons/react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const SRC_META = {
  mini_comp: { icon: Trophy, color: "text-red-400", dot: "bg-red-500" },
  powerkick: { icon: Barbell, color: "text-rose-400", dot: "bg-rose-500" },
  prive: { icon: Gift, color: "text-amber-400", dot: "bg-amber-500" },
};

const money = (n) => `${Number(n || 0).toLocaleString("fr-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} $`;

export default function OnlineRevenueDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/revenue/online-summary");
      setData(r.data);
    } catch { /* noop */ } finally { setLoading(false); }
  };

  useEffect(() => { document.title = "Revenus en ligne"; load(); }, []);

  const [syncing, setSyncing] = useState(false);
  const syncToCompta = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/revenue/sync-to-compta");
      toast.success(r.data?.message || "Synchronisé vers la Comptabilité Kaizen.");
    } catch {
      toast.error("Synchronisation impossible.");
    } finally { setSyncing(false); }
  };

  const [provisioning, setProvisioning] = useState(false);
  const provisionSubscribers = async () => {
    setProvisioning(true);
    try {
      const r = await api.post("/kaizen-online/resync-from-stripe");
      toast.success(r.data?.message || "Abonnés Kaizen en ligne provisionnés.");
    } catch {
      toast.error("Provisionnement impossible (clé Stripe LIVE requise en production).");
    } finally { setProvisioning(false); }
  };

  const exportCsv = async (scope) => {    try {
      const r = await api.get(`/revenue/online-export.csv?scope=${scope}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = scope === "month" ? "revenus-en-ligne-mois.csv" : "revenus-en-ligne-tout.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch { /* noop */ }
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString("fr-CA", { day: "2-digit", month: "short", year: "numeric" }); }
    catch { return iso; }
  };

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto" data-testid="online-revenue-dashboard">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <PageHeader
          title="Revenus en ligne"
          subtitle="Argent encaissé via Stripe — mini-compétitions, PowerKick Her, cours privés & cartes-cadeaux."
          icon={CurrencyDollar}
        />
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={provisionSubscribers} disabled={provisioning} data-testid="revenue-provision-subs-btn"
            className="flex items-center gap-2 text-xs uppercase tracking-wider text-[#c9a961] hover:text-amber-200 border border-[#c9a961]/40 bg-[#c9a961]/10 rounded-lg px-3 py-2 transition-colors disabled:opacity-50"
          >
            <Receipt size={14} /> {provisioning ? "Provision…" : "Provisionner abonnés Kaizen (Stripe)"}
          </button>
          <button
            onClick={syncToCompta} disabled={syncing} data-testid="revenue-sync-compta-btn"
            className="flex items-center gap-2 text-xs uppercase tracking-wider text-emerald-300 hover:text-emerald-200 border border-emerald-700/50 bg-emerald-600/10 rounded-lg px-3 py-2 transition-colors disabled:opacity-50"
          >
            <Receipt size={14} /> {syncing ? "Synchro…" : "Vers Comptabilité TPS/TVQ"}
          </button>
          <button
            onClick={() => exportCsv("month")} data-testid="revenue-export-month-btn"
            className="flex items-center gap-2 text-xs uppercase tracking-wider text-zinc-400 hover:text-emerald-400 border border-zinc-800 rounded-lg px-3 py-2 transition-colors"
          >
            <DownloadSimple size={14} /> CSV (mois)
          </button>
          <button
            onClick={() => exportCsv("all")} data-testid="revenue-export-all-btn"
            className="flex items-center gap-2 text-xs uppercase tracking-wider text-zinc-400 hover:text-emerald-400 border border-zinc-800 rounded-lg px-3 py-2 transition-colors"
          >
            <DownloadSimple size={14} /> CSV (tout)
          </button>
          <button
            onClick={load} data-testid="revenue-refresh-btn"
            className="flex items-center gap-2 text-xs uppercase tracking-wider text-zinc-400 hover:text-amber-400 border border-zinc-800 rounded-lg px-3 py-2 transition-colors"
          >
            <ArrowsClockwise size={14} /> Actualiser
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-zinc-500 text-sm mt-6">Chargement…</div>
      ) : !data ? (
        <div className="text-red-400 text-sm mt-6">Impossible de charger les revenus.</div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
            <StatCard
              label={`Encaissé en ${data.month_label}`}
              value={money(data.total_month_cad)}
              trend={`${data.count_month} paiement${data.count_month > 1 ? "s" : ""}`}
              icon={Receipt}
            />
            <StatCard
              label="Total encaissé (à vie)"
              value={money(data.total_all_time_cad)}
              trend={`${data.count_all_time} paiement${data.count_all_time > 1 ? "s" : ""}`}
              icon={CurrencyDollar}
            />
            <StatCard
              label="Panier moyen"
              value={money(data.count_all_time ? data.total_all_time_cad / data.count_all_time : 0)}
              icon={Trophy}
            />
          </div>

          {/* Tendance 6 mois */}
          {data.monthly_trend?.length > 0 && (
            <div className="mt-6 rounded-xl border border-zinc-800 bg-zinc-900/60 p-5" data-testid="revenue-trend-chart">
              <h2 className="text-sm uppercase tracking-widest text-zinc-400 mb-1">Tendance — 6 derniers mois</h2>
              <p className="text-xs text-zinc-500 mb-4">Total encaissé en ligne par mois (CAD).</p>
              <div style={{ width: "100%", height: 220 }}>
                <ResponsiveContainer>
                  <AreaChart data={data.monthly_trend} margin={{ top: 8, right: 12, left: 4, bottom: 0 }}>
                    <defs>
                      <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#10b981" stopOpacity={0.5} />
                        <stop offset="100%" stopColor="#10b981" stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272a" vertical={false} />
                    <XAxis dataKey="label" stroke="#71717a" fontSize={12} tickLine={false} axisLine={false} />
                    <YAxis stroke="#71717a" fontSize={11} tickLine={false} axisLine={false}
                      tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(1)}k$` : `${v}$`)} width={48} />
                    <Tooltip
                      contentStyle={{ background: "#18181b", border: "1px solid #3f3f46", borderRadius: 10, color: "#fafafa", fontSize: 13 }}
                      labelStyle={{ color: "#a1a1aa" }}
                      formatter={(v) => [money(v), "Encaissé"]} />
                    <Area type="monotone" dataKey="total_cad" stroke="#10b981" strokeWidth={2.5} fill="url(#revGrad)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Répartition par source */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
            {Object.entries(data.by_source).map(([key, s]) => {
              const meta = SRC_META[key] || {};
              const Icon = meta.icon || CurrencyDollar;
              return (
                <div key={key} className="rounded-xl border border-zinc-800 bg-zinc-900/60 p-5" data-testid={`revenue-source-${key}`}>
                  <div className="flex items-center gap-2 text-sm text-zinc-300">
                    <Icon size={18} weight="duotone" className={meta.color} />
                    <span>{s.label}</span>
                  </div>
                  <div className="text-2xl font-bold text-zinc-50 mt-3">{money(s.total_cad)}</div>
                  <div className="text-xs text-zinc-500 mt-1">
                    {s.count} paiement{s.count > 1 ? "s" : ""} · {money(s.month_cad)} ce mois-ci
                  </div>
                </div>
              );
            })}
          </div>

          {/* Derniers paiements */}
          <div className="mt-8">
            <h2 className="text-sm uppercase tracking-widest text-zinc-400 mb-3">Derniers paiements</h2>
            {data.recent_payments.length === 0 ? (
              <EmptyState
                title="Aucun paiement encaissé pour l'instant"
                description="Les paiements Stripe (compétitions, PowerKick Her, cours privés) apparaîtront ici dès qu'un client paie en ligne."
                icon={CurrencyDollar}
              />
            ) : (
              <div className="rounded-xl border border-zinc-800 overflow-hidden" data-testid="recent-payments-table">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-900 text-zinc-500 text-[11px] uppercase tracking-wider">
                    <tr>
                      <th className="text-left font-medium px-4 py-3">Date</th>
                      <th className="text-left font-medium px-4 py-3">Source</th>
                      <th className="text-left font-medium px-4 py-3">Description</th>
                      <th className="text-left font-medium px-4 py-3 hidden md:table-cell">Client</th>
                      <th className="text-right font-medium px-4 py-3">Montant</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-800">
                    {data.recent_payments.map((p, i) => {
                      const meta = SRC_META[p.source] || {};
                      return (
                        <tr key={i} className="hover:bg-zinc-900/50 transition-colors" data-testid={`payment-row-${i}`}>
                          <td className="px-4 py-3 text-zinc-400 whitespace-nowrap">{fmtDate(p.paid_at)}</td>
                          <td className="px-4 py-3">
                            <span className="inline-flex items-center gap-1.5 text-xs text-zinc-300">
                              <span className={`w-2 h-2 rounded-full ${meta.dot || "bg-zinc-500"}`} />
                              {data.by_source[p.source]?.label || p.source}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-zinc-200">{p.label}</td>
                          <td className="px-4 py-3 text-zinc-500 hidden md:table-cell truncate max-w-[180px]">{p.customer || "—"}</td>
                          <td className="px-4 py-3 text-right font-semibold text-emerald-400 whitespace-nowrap">{money(p.amount)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
